// src/contexts/OrgPromotionConfigContext.jsx
//used for add to library toggle to improve UX

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storageUtils'; // ← CACHE
import { bucketOf as rawBucketOf, promoteUfi as rawPromoteUfi, demoteUfi as rawDemoteUfi } from '../utils/assetPromotionConfig';

const Ctx = createContext(null);
export const useOrgPromotionConfig = () => useContext(Ctx);

const keyFor = (orgId) => `org_${orgId}_assetPromotionConfig`;           // ← CACHE

const normalize = (raw) => ({
  keys: Array.isArray(raw?.keys) ? raw.keys : [],
  arrayKeys: Array.isArray(raw?.arrayKeys) ? raw.arrayKeys : [],
  dateKeys: Array.isArray(raw?.dateKeys) ? raw.dateKeys : [],
});

export function OrgPromotionConfigProvider({ children, enabled = false }) {
  const { organizationId } = useAuth();
  const [cfg, setCfg] = useState(null);   // {keys, arrayKeys, dateKeys}
  const [ready, setReady] = useState(false);

  // Warm start from cache (optional, if you implemented it)
  useEffect(() => {
   if (!organizationId || !enabled) { setCfg(null); setReady(false); return; }
    const stored = safeGetItem(keyFor(organizationId));
    if (stored && typeof stored === 'object') {
      setCfg(normalize(stored));
     // optional: setReady(true); // treats cache as ready; snapshot reconciles
    }
 }, [organizationId, enabled]);

  // Live subscribe (source of truth)
  useEffect(() => {
   if (!organizationId || !enabled) { setCfg(null); setReady(false); return; }
    const ref = doc(db, 'organizations', organizationId);
    const unsub = onSnapshot(ref,
      (snap) => {
        const next = normalize(snap.data()?.assetPromotionConfig || null);
        setCfg(next);
        safeSetItem(keyFor(organizationId), next);                       // ← CACHE
        setReady(true);
      },
      () => { setCfg(normalize(null)); setReady(true); }
    );
    return () => unsub();
 }, [organizationId, enabled]);

  const bucketOf = useCallback((ufi) => (cfg ? rawBucketOf(cfg, ufi) : null), [cfg]);

  // optimistic local update; snapshot will reconcile
  const promoteUfi = useCallback(async (ufi, kind) => {
    if (!organizationId || !ufi) return;
    setCfg((prev) => {
      const base = normalize(prev);
      const next = {
        keys: base.keys.filter(x => x !== ufi),
        arrayKeys: base.arrayKeys.filter(x => x !== ufi),
        dateKeys: base.dateKeys.filter(x => x !== ufi),
      };
      if (kind === 'key')   next.keys.push(ufi);
      if (kind === 'array') next.arrayKeys.push(ufi);
      if (kind === 'date')  next.dateKeys.push(ufi);
      safeSetItem(keyFor(organizationId), next);                         // ← CACHE
      return next;
    });
    await rawPromoteUfi(organizationId, ufi, kind);
  }, [organizationId]);

  const demoteUfi = useCallback(async (ufi) => {
    if (!organizationId || !ufi) return;
    setCfg((prev) => {
      const base = normalize(prev);
      const next = {
        keys: base.keys.filter(x => x !== ufi),
        arrayKeys: base.arrayKeys.filter(x => x !== ufi),
        dateKeys: base.dateKeys.filter(x => x !== ufi),
      };
      safeSetItem(keyFor(organizationId), next);                         // ← CACHE
      return next;
    });
    await rawDemoteUfi(organizationId, ufi);
  }, [organizationId]);

  const value = useMemo(() => ({
    isReady: ready,
    cfg,
    bucketOf,
    promoteUfi,
    demoteUfi,
    // optional: expose an invalidator if you want a manual reset button
    invalidateCache: () => organizationId && safeRemoveItem(keyFor(organizationId)), // ← CACHE
  }), [ready, cfg, bucketOf, promoteUfi, demoteUfi, organizationId]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
